import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuth";

/**
 * Renders the panel only for signed-in admins.
 *
 * This is routing, not security. Anyone can bypass it with a debugger — what
 * actually protects the data is RLS plus the `is_admin()` check inside every
 * `admin_*` function. Keeping that distinction clear matters: the temptation
 * with a component like this is to start trusting it.
 *
 * Two behaviour changes from the previous version:
 *
 *   • A signed-in non-admin is told they don't have access, rather than being
 *     silently signed out and bounced to the login form — which looked
 *     identical to a wrong password and sent people round the loop again.
 *   • It no longer runs its own session and profile queries. AdminAuthProvider
 *     owns that, so Topbar and Dashboard don't each repeat it.
 */
export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const { identity, checking, signOut } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!checking && !identity) {
      navigate("/login", { replace: true });
    }
  }, [checking, identity, navigate]);

  if (checking) {
    return (
      <Centered>
        <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>Verifying access…</div>
      </Centered>
    );
  }

  if (!identity) return null;

  if (!identity.isAdmin) {
    return (
      <Centered>
        <div style={{ textAlign: "center", maxWidth: 380 }}>
          <ShieldAlert size={30} color="var(--danger)" />
          <div style={{ fontSize: 18, fontWeight: 850, marginTop: 14, letterSpacing: "-0.03em" }}>
            You don&apos;t have admin access
          </div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.6 }}>
            You&apos;re signed in as {identity.email}, but this account isn&apos;t an
            administrator. Ask an existing admin to grant access.
          </div>
          <button
            className="btn"
            style={{ marginTop: 20 }}
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </button>
        </div>
      </Centered>
    );
  }

  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
